import React, { useState, useEffect, useRef, useMemo } from 'react';
import useDebounce from '../../hooks/useDebounce';
import '../componentCss/webform.css';
import '../componentCss/sidebar.css';
import '../componentCss/healthcare-modern.css';
import {
  getFormListWithPermissionAction,
  getStaffListAction
} from '../../Action/modernAdminActions';
import toast, { Toaster } from 'react-hot-toast';
import "react-toastify/dist/ReactToastify.css";
import { Link } from 'react-router-dom';
import config from '../../config/config';
import DataTable from 'react-data-table-component';
import { Container, Row, Col, Modal } from "react-bootstrap";
import { FaEye, FaUserShield, FaFileAlt, FaUsers, FaUserCog, FaTimes, FaSearch, FaChevronDown, FaChevronRight, FaFolderOpen, FaFolder, FaEdit, FaPlus, FaTrash, FaPaperPlane, FaKey, FaCog } from "react-icons/fa";
import RoleAssignmentModal from './ModalComponents/RoleAssignmentModal';

const UserPermissions = () => {
  const [staffList, setStaffList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Pagination state for server-side pagination
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [formList, setFormList] = useState([]);
  const [isModelView, setIsModelView] = useState(false);
  const [currentStaffName, setCurrentStaffName] = useState('');
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const allDataRef = useRef();
  const singleRowRef = useRef();
  const [details, setDetails] = useState({
    fromDate: '',
    toDate: '',
    status: 'all',
    name: ''
  });

  // Role assignment modal state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleModalRefreshTrigger, setRoleModalRefreshTrigger] = useState(0);

  const handlePageChange = (page) => {
    console.log('📄 Page change requested:', page, 'Current:', currentPage + 1, 'Total pages:', totalPages);

    // Validate page range to prevent invalid requests
    if (page < 1) {
      console.warn('⚠️ Invalid page requested (too low):', page);
      return;
    }

    if (totalPages > 0 && page > totalPages) {
      console.warn('⚠️ Invalid page requested (too high):', page, 'Max:', totalPages);
      return;
    }

    const newCurrentPage = page - 1; // DataTable uses 1-based, we use 0-based
    console.log('✅ Valid page change:', { from: currentPage + 1, to: page, newCurrentPage });

    // Update state immediately to prevent rapid clicking issues
    setCurrentPage(newCurrentPage);

    // Call API immediately with fresh page value
    getStaffListAPI(page, rowsPerPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    console.log('📏 Rows per page change:', newRowsPerPage, 'Current:', rowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0); // Reset to first page when changing page size

    // Call API immediately with fresh values (avoid race condition)
    // Reset to page 1 when changing page size
    getStaffListAPI(1, newRowsPerPage);
  };

  const handleOpenEditView = (id) => {
    setIsModelView(true);
  };

  const handleViewClose = () => {
    setIsModelView(false);
    setSelectedRow(null);
    setFormList([]);
    setIsPermissionsLoading(false);
  };

  const handleOpenView = (data) => {
    setSelectedRow(data);
    setCurrentStaffName(data.full_name || `${data.first_name || ''} ${data.last_name || ''}`.trim());
    setFormList([]); // Clear previous data
    setPermissionCategories([]); // Clear previous categories
    getFormListAPI(data.id);
    setIsModelView(true);
  };

  // Role assignment modal handlers
  const handleOpenRoleAssignment = (user) => {
    setSelectedUser(user);
    setShowRoleModal(true);
  };

  const handleCloseRoleAssignment = () => {
    setShowRoleModal(false);
    setSelectedUser(null);
  };

  const handleRoleSaved = (updatedRoleNames) => {
    // Immediately update the staff list with new roles for better UX (optimistic update)
    if (selectedUser) {
      setStaffList(prevList =>
        prevList.map(user =>
          user.id === selectedUser.id
            ? { ...user, roles: updatedRoleNames || [] }
            : user
        )
      );
    }

    // Increment refresh trigger to force modal to fetch fresh data when reopened
    setRoleModalRefreshTrigger(prev => prev + 1);

    // Add a small delay to allow backend to process role changes, then refresh with cache busting
    setTimeout(() => {
      // Refresh the user list from server with cache busting to ensure fresh data
      getStaffListAPI(null, null, true); // bustCache = true
    }, 300);

    // If permissions modal is open, refresh the permissions for current user
    if (isModelView && selectedRow && selectedRow.id) {
      console.log('Refreshing permissions for user after role change:', selectedRow.id);
      // Clear existing data to show loading state
      setFormList([]);
      setPermissionCategories([]);
      // Reload permissions with cache busting
      getFormListAPI(selectedRow.id, true); // Pass true for cache busting
    }

    toast.success('User roles updated successfully!');
  };

  // Category management functions
  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const expandAllCategories = () => {
    const categories = permissionCategories.reduce((acc, category) => {
      acc[category] = false; // false means expanded
      return acc;
    }, {});
    setCollapsedCategories(categories);
  };

  const collapseAllCategories = () => {
    const categories = permissionCategories.reduce((acc, category) => {
      acc[category] = true; // true means collapsed
      return acc;
    }, {});
    setCollapsedCategories(categories);
  };

  const getCategoryIcon = (category) => {
    const categoryIcons = {
      'General': FaFileAlt,
      'Patient Management': FaUsers,
      'Reports': FaFileAlt,
      'Administration': FaUserShield,
      'System': FaUserCog
    };
    return categoryIcons[category] || FaFileAlt;
  };

  // Group and filter permissions by category
  const getGroupedPermissions = () => {
    const accessibleForms = formList.filter(data => data.isAccess === 1);

    // Apply search filter
    const filteredForms = accessibleForms.filter(data => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        (data.name && data.name.toLowerCase().includes(searchLower)) ||
        (data.description && data.description.toLowerCase().includes(searchLower)) ||
        (data.departmentName && data.departmentName.toLowerCase().includes(searchLower))
      );
    });

    // Apply category filter
    const categoryFilteredForms = selectedCategory === 'all'
      ? filteredForms
      : filteredForms.filter(data => data.departmentName === selectedCategory);

    // Group by category
    const grouped = categoryFilteredForms.reduce((acc, form) => {
      const category = form.departmentName || 'General';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(form);
      return acc;
    }, {});

    return grouped;
  };

  // Note: Comprehensive permissions API removed - only basic permission viewing is supported

  // Helper function to format category names for better UI display (using service method)
  const formatCategoryName = (categoryName) => {
    // Use the RBAC service's formatCategoryName method
    // For now, include local implementation to avoid service import in component
    if (!categoryName || categoryName === 'General') return 'General';

    const categoryMappings = {
      'clinical_operations': 'Clinical Operations',
      'patient_management': 'Patient Management',
      'medication_management': 'Medication Management',
      'user_management': 'User Management',
      'administrative': 'Administrative',
      'system_configuration': 'System Configuration',
      'reporting': 'Reporting',
      'audit': 'Audit',
      'document_management': 'Document Management',
      'scheduling': 'Scheduling'
    };

    const lowerCaseName = categoryName.toLowerCase();
    if (categoryMappings[lowerCaseName]) {
      return categoryMappings[lowerCaseName];
    }

    return categoryName
      .split(/[_-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to get action configuration
  const getActionConfig = (action) => {
    const actionConfigs = {
      'read': { icon: FaEye, color: 'primary', label: 'Read' },
      'create': { icon: FaPlus, color: 'success', label: 'Create' },
      'update': { icon: FaEdit, color: 'warning', label: 'Update' },
      'edit': { icon: FaEdit, color: 'warning', label: 'Edit' },
      'delete': { icon: FaTrash, color: 'danger', label: 'Delete' },
      'submit': { icon: FaPaperPlane, color: 'info', label: 'Submit' },
      'search': { icon: FaSearch, color: 'secondary', label: 'Search' },
      'approve': { icon: FaUserShield, color: 'success', label: 'Approve' },
      'administer': { icon: FaCog, color: 'primary', label: 'Administer' },
      'safety_check': { icon: FaUserShield, color: 'warning', label: 'Safety Check' },
      'analytics': { icon: FaEye, color: 'info', label: 'Analytics' },
      'verify': { icon: FaUserShield, color: 'success', label: 'Verify' },
      'export': { icon: FaFileAlt, color: 'secondary', label: 'Export' },
      'write': { icon: FaEdit, color: 'primary', label: 'Write' }
    };

    return actionConfigs[action] || { icon: FaKey, color: 'dark', label: action.charAt(0).toUpperCase() + action.slice(1) };
  };

  // Component to render action badges
  const ActionBadges = ({ actions = [] }) => {
    if (!actions || actions.length === 0) {
      return <span className="badge bg-light text-muted">No Actions</span>;
    }

    return (
      <div className="d-flex flex-wrap gap-1">
        {actions.map((action, index) => {
          const config = getActionConfig(action);
          const IconComponent = config.icon;

          return (
            <span
              key={index}
              className={`badge bg-${config.color} text-white d-flex align-items-center gap-1`}
              style={{ fontSize: '0.75rem' }}
              title={`${config.label} permission`}
            >
              <IconComponent size={10} />
              {config.label}
            </span>
          );
        })}
      </div>
    );
  };

  let getFormListAPI = async (userId, bustCache = false) => {
    try {
      setIsPermissionsLoading(true);

      // Add cache busting parameter when needed
      const params = { userId: userId };
      if (bustCache) {
        params._cacheBuster = Date.now(); // Add timestamp to force fresh data
        console.log('Using cache buster for permissions request:', params._cacheBuster);
      }

      let res = await getFormListWithPermissionAction(params);
      if (res.success) {
        // Modern API returns data directly without encryption
        const rawData = res.data || [];

        // The new RBAC service should already provide properly transformed data
        // But we'll add a safety transformation in case there are any edge cases
        const transformedData = rawData.map(item => {
          // Preserve all the data from the service transformation
          const baseItem = {
            ...item,
            // Ensure actions array exists and is preserved
            actions: item.actions || [],
            // Ensure department name is properly formatted
            departmentName: item.departmentName || formatCategoryName(item.category || item.module || 'General'),
            // Ensure resource type is set
            resource_type: item.resource_type || 'form',
            // Ensure boolean flags are properly set
            is_active: item.is_active !== undefined ? item.is_active : true,
            is_system: item.is_system !== undefined ? item.is_system : false,
            // Ensure access flags are properly calculated
            isAccess: item.isAccess !== undefined ? item.isAccess : (item.actions && item.actions.length > 0 ? 1 : 0),
            canAccess: item.canAccess !== undefined ? item.canAccess : !!(item.actions && item.actions.length > 0)
          };

          return baseItem;
        });

        // Extract unique categories for filtering
        const categories = [...new Set(transformedData.map(item => item.departmentName || 'General'))];
        setPermissionCategories(categories.sort());

        setFormList(transformedData);
      } else {
        setFormList([]);
        toast.error(res.message || "Failed to load permissions");
      }
    } catch (error) {
      setFormList([]);
      toast.error("An error occurred while loading permissions");
    } finally {
      setIsPermissionsLoading(false);
    }
  };

  let getStaffListAPI = async (overridePage = null, overrideRowsPerPage = null, bustCache = false) => {
    if (details.fromDate && !details.toDate) {
      toast.error('Please select to date');
      return;
    }

    if (!details.fromDate && details.toDate) {
      toast.error('Please select from date');
      return;
    }

    try {
      setIsLoading(true);

      // Use provided values or fall back to state
      const pageToUse = overridePage !== null ? overridePage : (currentPage + 1);
      const rowsPerPageToUse = overrideRowsPerPage !== null ? overrideRowsPerPage : rowsPerPage;

      console.log('🔄 API Call Parameters:', {
        pageToUse,
        rowsPerPageToUse,
        currentPageState: currentPage,
        rowsPerPageState: rowsPerPage,
        overridePage,
        overrideRowsPerPage,
        searchTerm: debouncedSearchTerm || searchTerm,
        bustCache
      });

      // Combine details with search term for backend API
      const searchParams = {
        ...details,
        search: debouncedSearchTerm || searchTerm || undefined,
        page: pageToUse,
        page_size: rowsPerPageToUse
      };

      // Add cache busting parameter when needed
      if (bustCache) {
        searchParams._t = Date.now(); // Timestamp to force fresh data
        console.log('🔄 Cache busting enabled with timestamp:', searchParams._t);
      }

      // Remove undefined and empty values
      const cleanParams = Object.fromEntries(
        Object.entries(searchParams).filter(([_, value]) =>
          value !== undefined && value !== '' && value !== 'all'
        )
      );

      let res = await getStaffListAction(cleanParams);
      setIsLoading(false);
      if (res.success && res.data) {
        console.log('Raw API response structure:', {
          hasData: !!res.data,
          hasDataItems: !!(res.data && res.data.items),
          hasDataMeta: !!(res.data && res.data.meta),
          hasTopLevelItems: !!res.items,
          hasTopLevelTotal: !!res.total,
          dataType: typeof res.data,
          itemsLength: res.data?.items?.length || res.data?.length || res.items?.length || 0
        });

        // Extract items array - try nested structure first, then fallback to flattened
        let staffItems = [];
        let paginationMeta = {};

        if (res.data.items && Array.isArray(res.data.items)) {
          // Preferred: Nested structure {data: {items: [...], meta: {...}}}
          staffItems = res.data.items;
          paginationMeta = res.data.meta || {};
          console.log('✅ Using nested structure:', { itemsCount: staffItems.length, meta: paginationMeta });
        } else if (Array.isArray(res.data)) {
          // Fallback: Direct array in data {data: [...]}
          staffItems = res.data;
          paginationMeta = {
            total: res.total || staffItems.length,
            page: res.page || 1,
            total_pages: res.total_pages || 1,
            has_next: res.has_next || false,
            has_prev: res.has_prev || false
          };
          console.log('⚠️ Using flattened structure fallback:', { itemsCount: staffItems.length, meta: paginationMeta });
        } else if (res.items && Array.isArray(res.items)) {
          // Additional fallback: Top-level items array
          staffItems = res.items;
          paginationMeta = {
            total: res.total || staffItems.length,
            page: res.page || 1,
            total_pages: res.total_pages || 1,
            has_next: res.has_next || false,
            has_prev: res.has_prev || false
          };
          console.log('⚠️ Using top-level items fallback:', { itemsCount: staffItems.length, meta: paginationMeta });
        }

        setStaffList(staffItems);

        // Set pagination metadata
        setTotalRows(paginationMeta.total || 0);
        setTotalPages(paginationMeta.total_pages || 1);
        setHasNextPage(paginationMeta.has_next || false);
        setHasPrevPage(paginationMeta.has_prev || false);

        // Ensure current page state matches backend response
        const backendPage = paginationMeta.page || 1;
        const expectedCurrentPage = backendPage - 1; // Convert to 0-based

        if (expectedCurrentPage !== currentPage) {
          console.log('🔄 Syncing page state with backend:', {
            frontendPage: currentPage + 1,
            backendPage: backendPage,
            updating: true
          });
          setCurrentPage(expectedCurrentPage);
        }

        console.log('✅ Final pagination state:', {
          totalRows: paginationMeta.total,
          currentPage: backendPage,
          frontendPageState: currentPage + 1,
          totalPages: paginationMeta.total_pages,
          hasNext: paginationMeta.has_next,
          hasPrev: paginationMeta.has_prev,
          itemsCount: staffItems.length
        });
      } else {
        setStaffList([]);
        setTotalRows(0);
        setTotalPages(0);
        setHasNextPage(false);
        setHasPrevPage(false);
        toast.error(res.message || "Failed to load staff list");
      }
    } catch (error) {
      setIsLoading(false);
      setStaffList([]);
      setTotalRows(0);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPrevPage(false);
      toast.error("An error occurred while loading staff list");
    }
  };

  let getStaffListAPIForFilter = async () => {
    // Reset all filter states to initial values
    setDetails({
      fromDate: '',
      toDate: '',
      status: 'all',
      name: ''
    });

    // Clear search term
    setSearchTerm('');

    // Reset pagination
    setCurrentPage(0);

    // Show loading state
    setIsLoading(true);

    try {
      // Fetch fresh unfiltered staff list from server
      let res = await getStaffListAction({
        page: 1,
        page_size: rowsPerPage
      });

      setIsLoading(false);
      if (res.success && res.data) {
        // Extract items array - use same robust logic as main API call
        let staffItems = [];
        let paginationMeta = {};

        if (res.data.items && Array.isArray(res.data.items)) {
          staffItems = res.data.items;
          paginationMeta = res.data.meta || {};
        } else if (Array.isArray(res.data)) {
          staffItems = res.data;
          paginationMeta = {
            total: res.total || staffItems.length,
            page: res.page || 1,
            total_pages: res.total_pages || 1,
            has_next: res.has_next || false,
            has_prev: res.has_prev || false
          };
        } else if (res.items && Array.isArray(res.items)) {
          staffItems = res.items;
          paginationMeta = {
            total: res.total || staffItems.length,
            page: res.page || 1,
            total_pages: res.total_pages || 1,
            has_next: res.has_next || false,
            has_prev: res.has_prev || false
          };
        }

        setStaffList(staffItems);
        setTotalRows(paginationMeta.total || 0);
        setTotalPages(paginationMeta.total_pages || 1);
        setHasNextPage(paginationMeta.has_next || false);
        setHasPrevPage(paginationMeta.has_prev || false);

        toast.success('Filters reset successfully');
      } else {
        setStaffList([]);
        setTotalRows(0);
        setTotalPages(0);
        setHasNextPage(false);
        setHasPrevPage(false);
        toast.error(res.message || "Failed to reset filters");
      }
    } catch (error) {
      setIsLoading(false);
      setStaffList([]);
      setTotalRows(0);
      setTotalPages(0);
      setHasNextPage(false);
      setHasPrevPage(false);
      toast.error("An error occurred while resetting filters");
    }
  };

  // Note: Direct permission editing has been removed. 
  // Users can only view their effective permissions here.
  // Role assignment should be done through the Edit User functionality.

  const searchByNameHandler = (e) => {
    const { name, value } = e.target;
    setDetails((old) => {
      return { ...old, [name]: value };
    });
  };

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  // Note: filteredFormList removed as it's not used in this component

  // Client-side filtering for immediate feedback while typing (before Apply is clicked)
  const filteredStaffList = useMemo(() => {
    // If no immediate search term, return server data as-is
    if (!searchTerm) {
      return staffList;
    }

    const searchLower = searchTerm.toLowerCase();

    return staffList.filter((user) => {
      // Match against user name
      const fullName = (user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim()).toLowerCase();

      // Match against email
      const email = (user.email || '').toLowerCase();

      // Match against assigned role names
      let roleText = '';
      if (user.roles && Array.isArray(user.roles)) {
        roleText = user.roles.join(' ').toLowerCase();
      }

      return fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        roleText.includes(searchLower);
    });
  }, [staffList, searchTerm]); // Note: using searchTerm instead of debouncedSearchTerm for instant feedback

  // Note: Direct permission editing modal has been removed.
  // This component now only shows permission information in read-only mode.

  useEffect(() => {
    getStaffListAPI();
  }, []);

  useEffect(() => {
    setSelectedRow(null);
  }, [currentPage]);

  const columns = [
    {
      name: "#",
      cell: (row, index) => {
        return index + 1 + currentPage * rowsPerPage;
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Name",
      selector: (row) => row.full_name || `${row.first_name || ''} ${row.last_name || ''}`.trim(),
      sortable: true,
      width: "15%"
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      width: "20%"
    },
    {
      name: 'Roles',
      cell: (row) => (
        <div className='d-flex flex-wrap'>
          {row.roles && row.roles.length > 0 ? (
            row.roles.map((role, index) => (
              <span key={index} className="healthcare-status-badge healthcare-status-active me-1 mb-1">
                {role}
              </span>
            ))
          ) : (
            <span className="healthcare-status-badge healthcare-status-inactive">
              No Roles
            </span>
          )}
        </div>
      ),
      sortable: true,
      width: '30%',
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="d-flex gap-2">
          <button
            onClick={() => handleOpenView(row)}
            title="View Permissions"
            className="healthcare-action-btn healthcare-action-view"
          >
            <FaEye size={14} />
            View Permissions
          </button>
          <button
            onClick={() => handleOpenRoleAssignment(row)}
            title="Assign Roles"
            className="healthcare-action-btn healthcare-action-edit"
          >
            <FaUserCog size={14} />
            Assign Roles
          </button>
        </div>
      ),
      sortable: true,
      width: "25%"
    },
  ];

  return (
    <>
      {/* <Dashboardheader />
      <Toaster /> */}
      {/* <div className="wrapper">
        <Sidebar /> */}
      <div id="content" className="healthcare-container">
        <Container fluid className="p-4">
          <Row>
            <Col lg={12} md={12} sm={12} className="">
              <div className="healthcare-card">
                <div className="healthcare-header">
                  <h5>
                    <FaUserShield size={24} />
                    User Permissions
                  </h5>
                  <div className="subtitle">
                    Manage user roles and form-level permissions
                  </div>
                </div>
                <div className="p-4">
                  <div className="healthcare-table-header">
                    <div className="healthcare-table-title">
                      <FaUsers />
                      User Management
                    </div>
                    <div className="healthcare-table-subtitle">
                      Assign roles and configure permissions for system users
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={12} sm={12} className="mt-3">
              <div style={{ float: "right" }}>

              </div>
              <div className="healthcare-card mt-4">
                <div className="p-4">
                  <div className="healthcare-table-header">
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                      <div className="healthcare-search-container position-relative">
                        <input
                          type="text"
                          className="healthcare-search-input"
                          placeholder="Search by name, email, or roles..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ paddingRight: searchTerm ? '35px' : '12px' }}
                        />
                        {searchTerm && (
                          <button
                            className="btn btn-sm position-absolute"
                            style={{
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              border: 'none',
                              background: 'transparent',
                              color: '#6c757d',
                              padding: '2px 4px'
                            }}
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                          >
                            <FaTimes size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="healthcare-table-actions">
                      <div className="d-flex gap-2">
                        <input
                          type="date"
                          name="fromDate"
                          className="form-control"
                          value={details.fromDate}
                          onChange={searchByNameHandler}
                          placeholder="From Date"
                        />
                        <input
                          type="date"
                          name="toDate"
                          className="form-control"
                          value={details.toDate}
                          onChange={searchByNameHandler}
                          placeholder="To Date"
                        />
                        <button
                          className="healthcare-btn healthcare-btn-secondary"
                          onClick={getStaffListAPIForFilter}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Resetting...' : 'Reset'}
                        </button>
                        <button
                          className="healthcare-btn healthcare-btn-primary"
                          onClick={getStaffListAPI}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Loading...' : 'Apply'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <br />

              <div className="healthcare-table-container mt-4">
                {isLoading ? (
                  <div className="healthcare-loading">
                    <div className="healthcare-loading-spinner"></div>
                    <div className="healthcare-loading-text">Loading users...</div>
                  </div>
                ) : (
                  <DataTable
                    key={`pagination-${currentPage}-${rowsPerPage}-${totalRows}`}
                    columns={columns}
                    data={filteredStaffList}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    paginationDefaultPage={currentPage + 1}
                    paginationRowsPerPageOptions={[10, 15, 25, 50, 100]}
                    paginationComponentOptions={{
                      rowsPerPageText: 'Rows per page:',
                      rangeSeparatorText: 'of',
                      noRowsPerPage: false,
                      selectAllRowsItem: false,
                      selectAllRowsItemText: 'All'
                    }}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    progressPending={isLoading}
                    noDataComponent={
                      <div className="healthcare-loading">
                        <FaUsers size={48} className="text-muted mb-3" />
                        <div className="healthcare-loading-text text-muted">
                          {debouncedSearchTerm ? `No users found matching "${debouncedSearchTerm}"` : 'No users found'}
                        </div>
                        {debouncedSearchTerm && (
                          <button
                            className="btn btn-link btn-sm text-primary mt-2"
                            onClick={() => setSearchTerm('')}
                          >
                            Clear search to see all users
                          </button>
                        )}
                      </div>
                    }
                  />
                )}
              </div>
              {/*------------------------------- Section For Showing All Data For Print--------------------------------------------------- */}


              {/*------------------------------- Section For Showing Single Data For Print--------------------------------------------------- */}


            </Col>
          </Row>



          {/* View Permission Details Modal */}
          <Modal
            show={isModelView}
            onHide={handleViewClose}
            dialogClassName="modal-lg"
            aria-labelledby="permission-view-modal-title"
          >
            <Modal.Header closeButton className="border-0">
              <Modal.Title id="permission-view-modal-title" className="d-flex align-items-center gap-2">
                <FaEye className="text-primary" />
                <span>Permission Details</span>
                {selectedRow && (
                  <small className="text-muted ms-2">
                    {selectedRow.full_name || `${selectedRow.first_name || ''} ${selectedRow.last_name || ''}`.trim()}
                  </small>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">

              {/* User Permission Summary */}
              {selectedRow && (
                <div className="bg-light border-bottom p-4 mb-0">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="d-flex align-items-center">
                        <div className="healthcare-icon-circle bg-primary-subtle me-3">
                          <FaUserShield size={18} className="text-primary" />
                        </div>
                        <div>
                          <h6 className="mb-1 text-dark fw-semibold">
                            {selectedRow.full_name || `${selectedRow.first_name || ''} ${selectedRow.last_name || ''}`.trim()}
                          </h6>
                          <div className="text-muted small">
                            <span className="me-3">
                              <strong>Email:</strong> {selectedRow.email}
                            </span>
                            {selectedRow.roles && selectedRow.roles.length > 0 && (
                              <span>
                                <strong>Roles:</strong> {selectedRow.roles.join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 text-end">
                      <div className="d-flex justify-content-end gap-3">
                        <div className="text-center">
                          <div className="h5 mb-0 text-primary fw-bold">
                            {formList.filter(f => f.isAccess === 1).length}
                          </div>
                          <small className="text-muted">Accessible</small>
                        </div>
                        <div className="text-center">
                          <div className="h5 mb-0 text-success fw-bold">
                            {formList.filter(f => f.actions && f.actions.includes('create')).length}
                          </div>
                          <small className="text-muted">Can Create</small>
                        </div>
                        <div className="text-center">
                          <div className="h5 mb-0 text-warning fw-bold">
                            {formList.filter(f => f.actions && f.actions.includes('update')).length}
                          </div>
                          <small className="text-muted">Can Update</small>
                        </div>
                        <div className="text-center">
                          <div className="h5 mb-0 text-secondary fw-bold">
                            {formList.length}
                          </div>
                          <small className="text-muted">Total</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="healthcare-card border-0 shadow-sm m-4">
                <div className="healthcare-table-header border-bottom pb-3 mb-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="healthcare-table-title text-primary">
                        <FaFileAlt className="me-2" />
                        Form Access Permissions
                      </div>
                      <div className="healthcare-table-subtitle text-muted">
                        Forms and modules this user has permission to access
                      </div>
                      {formList.filter(f => f.isAccess === 1).length > 0 && (
                        <div className="mt-3">
                          {/* Action Legend */}
                          <div className="mb-3 p-2 bg-light rounded border">
                            <div className="small text-muted mb-1">
                              <FaKey size={12} className="me-1" />
                              Permission Types:
                            </div>
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                              <span className="badge bg-primary text-white d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                <FaEye size={8} />
                                Read
                              </span>
                              <span className="badge bg-success text-white d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                <FaPlus size={8} />
                                Create
                              </span>
                              <span className="badge bg-warning text-white d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                <FaEdit size={8} />
                                Update
                              </span>
                              <span className="badge bg-danger text-white d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                <FaTrash size={8} />
                                Delete
                              </span>
                              <span className="badge bg-info text-white d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                <FaPaperPlane size={8} />
                                Submit
                              </span>
                              <span className="badge bg-secondary text-white d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                <FaUserShield size={8} />
                                Approve
                              </span>
                            </div>
                          </div>

                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={expandAllCategories}
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                            >
                              <FaChevronDown size={10} className="me-1" />
                              Expand All
                            </button>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={collapseAllCategories}
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                            >
                              <FaChevronRight size={10} className="me-1" />
                              Collapse All
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {formList.length > 0 && (
                      <div className="flex-shrink-0 ms-3">
                        <div className="d-flex gap-2" style={{ minWidth: '400px' }}>
                          <div className="healthcare-search-container position-relative flex-grow-1">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Search forms..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              style={{ paddingLeft: '35px', paddingRight: searchTerm ? '35px' : '12px' }}
                            />
                            <FaSearch
                              size={12}
                              className="position-absolute text-muted"
                              style={{
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}
                            />
                            {searchTerm && (
                              <button
                                className="btn btn-sm position-absolute"
                                style={{
                                  right: '8px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  border: 'none',
                                  background: 'transparent',
                                  color: '#6c757d',
                                  padding: '2px 4px'
                                }}
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                              >
                                <FaTimes size={10} />
                              </button>
                            )}
                          </div>
                          <select
                            className="form-select form-select-sm"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            style={{ minWidth: '150px' }}
                          >
                            <option value="all">All Categories</option>
                            {permissionCategories.map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-0">
                  {(() => {
                    // Show loading spinner while permissions are being fetched
                    if (isPermissionsLoading) {
                      return (
                        <div className="healthcare-loading modal-loading bg-light">
                          <div className="healthcare-loading-spinner"></div>
                          <div className="healthcare-loading-text">Loading permissions...</div>
                        </div>
                      );
                    }

                    const groupedPermissions = getGroupedPermissions();
                    const categoryKeys = Object.keys(groupedPermissions);
                    const totalForms = formList.filter(data => data.isAccess === 1).length;

                    if (totalForms === 0) {
                      return (
                        <div className="text-center py-5 bg-light">
                          <div className="healthcare-empty-state">
                            <div className="healthcare-icon-circle bg-warning-subtle mx-auto mb-3" style={{ width: '64px', height: '64px' }}>
                              <FaFileAlt size={24} className="text-warning" />
                            </div>
                            <h6 className="text-dark mb-2">No Form Permissions Assigned</h6>
                            <p className="text-muted mb-0">This user currently has no access permissions for any forms or modules in the system.</p>
                            <small className="text-muted d-block mt-2">Contact an administrator to assign form permissions to this user.</small>
                          </div>
                        </div>
                      );
                    }

                    if (categoryKeys.length === 0 && (searchTerm || selectedCategory !== 'all')) {
                      return (
                        <div className="text-center py-5 bg-light">
                          <div className="healthcare-empty-state">
                            <div className="healthcare-icon-circle bg-info-subtle mx-auto mb-3" style={{ width: '64px', height: '64px' }}>
                              <FaSearch size={24} className="text-info" />
                            </div>
                            <h6 className="text-dark mb-2">No Forms Match Your Criteria</h6>
                            <p className="text-muted mb-0">No accessible forms found matching your search or category filter</p>
                            <div className="d-flex gap-2 justify-content-center mt-3">
                              {searchTerm && (
                                <button
                                  className="btn btn-link btn-sm text-primary"
                                  onClick={() => setSearchTerm('')}
                                >
                                  Clear search
                                </button>
                              )}
                              {selectedCategory !== 'all' && (
                                <button
                                  className="btn btn-link btn-sm text-primary"
                                  onClick={() => setSelectedCategory('all')}
                                >
                                  Show all categories
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {categoryKeys.sort().map((category) => {
                          const forms = groupedPermissions[category];
                          const isCollapsed = collapsedCategories[category];
                          const CategoryIcon = getCategoryIcon(category);

                          return (
                            <div key={category} className="border-bottom">
                              <div
                                className="d-flex align-items-center justify-content-between p-3 bg-light hover-bg-secondary cursor-pointer"
                                onClick={() => toggleCategory(category)}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    {isCollapsed ? (
                                      <FaChevronRight size={12} className="text-muted" />
                                    ) : (
                                      <FaChevronDown size={12} className="text-muted" />
                                    )}
                                  </div>
                                  <div className="healthcare-icon-circle bg-primary-subtle me-3">
                                    <CategoryIcon size={14} className="text-primary" />
                                  </div>
                                  <div>
                                    <h6 className="mb-0 fw-semibold text-dark">{category}</h6>
                                    <small className="text-muted">
                                      {forms.length} form{forms.length !== 1 ? 's' : ''} accessible
                                    </small>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-primary text-white rounded-pill px-2 py-1">
                                    {forms.length}
                                  </span>
                                </div>
                              </div>

                              {!isCollapsed && (
                                <div className="bg-white">
                                  {forms.map((form, index) => (
                                    <div
                                      key={form.id}
                                      className={`d-flex align-items-center justify-content-between p-3 border-bottom-light ${index === forms.length - 1 ? '' : 'border-bottom'
                                        }`}
                                      style={{ marginLeft: '2rem' }}
                                    >
                                      <div className="d-flex align-items-center flex-grow-1">
                                        <div className="healthcare-icon-circle bg-success-subtle me-3">
                                          <FaFileAlt size={12} className="text-success" />
                                        </div>
                                        <div className="flex-grow-1">
                                          <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className="fw-medium text-dark">{form.name}</span>
                                            {form.is_system && (
                                              <span className="badge bg-dark text-white" style={{ fontSize: '0.65rem' }}>
                                                <FaCog size={8} className="me-1" />
                                                System
                                              </span>
                                            )}
                                            {form.resource_type && form.resource_type !== 'form' && (
                                              <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem' }}>
                                                {form.resource_type}
                                              </span>
                                            )}
                                          </div>
                                          {form.id && (
                                            <small className="text-muted d-block" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                              ID: {form.id}
                                            </small>
                                          )}
                                          {form.description && (
                                            <small className="text-muted d-block mt-1">{form.description}</small>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-end" style={{ minWidth: '200px' }}>
                                        <ActionBadges actions={form.actions} />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>

            </Modal.Body>
            <Modal.Footer className="border-0">
              <button
                onClick={handleViewClose}
                className="healthcare-btn healthcare-btn-secondary"
              >
                Close
              </button>
            </Modal.Footer>
          </Modal>

          {/* Note: Direct permission editing modal has been removed. 
               Users can only view form permissions in read-only mode.
               Role assignment should be done through the Edit User functionality. */}

          {/* Role Assignment Modal */}
          <RoleAssignmentModal
            show={showRoleModal}
            user={selectedUser}
            onHide={handleCloseRoleAssignment}
            onSaved={handleRoleSaved}
            refreshTrigger={roleModalRefreshTrigger}
          />

        </Container>
      </div>

      {/* </div> */}
    </>
  );
};

export default UserPermissions;
